// Point d'entrée unique pour tous les appels au backend.
// Centraliser ici évite de disperser les URLs dans les composants
// et facilite les changements futurs (auth, headers, gestion d'erreur).

import axios from "axios";
import { readJson, writeJson, clearUserCache } from "./offlineStore";
import { clearQueue } from "./offlineQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hors ligne, étape 2 : SANS ce timeout, une requête vers un serveur
// injoignable (vraiment hors ligne, pas juste un 404/500) peut rester en
// attente très longtemps avant qu'axios ne rejette — le réseau mobile en
// avion/zone blanche ne "refuse" pas la connexion instantanément comme un
// port fermé en local, il peut simplement ne jamais répondre. Sans ça, le
// repli sur le cache local (withOfflineFallback) n'était déclenché qu'après
// une attente interminable, ce qui donnait l'impression que l'appli "ne
// trouve plus rien" alors qu'elle attendait juste une réponse qui ne
// viendrait jamais. Les appels qui ont vraiment besoin de plus (le
// scanner) gardent déjà leur propre `timeout` explicite, qui prime sur celui-ci.
axios.defaults.timeout = 10000;

// Hors ligne, étape 2 : réseau d'abord (les données peuvent changer — prix,
// nouvelles cartes), repli sur le cache local UNIQUEMENT si le réseau
// échoue. Écrit toujours le résultat frais en cache après un succès
// (write-through, best-effort — pas attendu), pour que la prochaine coupure
// retrouve les dernières données vues.
async function withOfflineFallback(networkCall, cacheKey) {
  try {
    const data = await networkCall();
    writeJson(cacheKey, data);
    return data;
  } catch (err) {
    const cached = await readJson(cacheKey);
    if (cached !== null) return cached;
    throw err;
  }
}

// ── Gestion du token d'authentification ──
// Le token est stocké en mémoire ET dans localStorage (pour survivre au refresh).
// Intercepteur axios : ajoute automatiquement le token à chaque requête.
const TOKEN_KEY = "poketracker_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Ajoute le token Bearer à chaque requête sortante
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Si le backend renvoie 401 (token invalide/expiré), on nettoie et on recharge
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      clearToken();
      // Recharge la page pour revenir à l'écran de login
      if (!window.location.pathname.includes("login")) {
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// ── Authentification ──
export async function login(username, password) {
  const res = await axios.post(`${API}/auth/login`, { username, password });
  // Changement de compte sur le même appareil : le cache local de collection
  // (stats, progression, statuts) et la file d'écritures hors ligne
  // appartiennent au compte PRÉCÉDENT — les purger avant de poser le nouveau
  // token, sinon un simple timeout réseau afficherait ses chiffres à lui
  // (bug du 2026-09-14), et ses écritures en attente partiraient sur le
  // mauvais compte. Le catalogue (séries, sets, images) n'est pas concerné :
  // il est le même pour tout le monde, on le garde.
  const previous = await readJson("me.json");
  const prevName = (previous && previous.username) || "";
  if (prevName.toLowerCase() !== String(username).toLowerCase()) {
    await clearUserCache();
    await clearQueue();
  }
  setToken(res.data.access_token);
  return res.data;
}

// Hors ligne, étape 2 : mis en cache comme le reste (write-through), MAIS ne
// consulte le cache que si le réseau est vraiment injoignable (pas de
// réponse serveur) — un vrai 401 (token révoqué/expiré, confirmé par le
// backend) ne doit jamais être masqué par une session en cache, sinon un
// appareil qui retrouve le réseau resterait "connecté" localement avec un
// token que le serveur a explicitement rejeté.
export async function fetchMe() {
  try {
    const res = await axios.get(`${API}/auth/me`);
    writeJson("me.json", res.data);
    return res.data;
  } catch (err) {
    if (err.response) throw err;
    const cached = await readJson("me.json");
    if (cached) return cached;
    throw err;
  }
}

export function logout() {
  clearToken();
}

// ── Séries & sets ──
export async function fetchSeriesWithSets() {
  return withOfflineFallback(async () => {
    const res = await axios.get(`${API}/series-with-sets`);
    return res.data.series || [];
  }, "series-with-sets.json");
}

// Force la reconstruction de la liste séries/sets côté backend (sans attendre
// l'expiration du cache 24 h) — pour faire apparaître une série qui vient de
// sortir. N'invalide que ce cache-là, pas les cartes ni la collection.
export async function refreshSeriesWithSets() {
  const res = await axios.post(`${API}/series-with-sets/refresh`);
  return res.data;
}

// ── Suivi Pokécardex (Réglages) ──
// Le backend lit les pages Pokécardex lui-même : nouvelles séries à créer et
// cartes qu'ils ont en plus sur le bloc le plus récent (promos en cours…).
// Sans `refresh`, renvoie le dernier résultat mémorisé côté serveur.
export async function fetchPokecardexWatch(refresh = false) {
  const res = await axios.get(`${API}/pokecardex/watch`, { params: refresh ? { refresh: 1 } : {} });
  return res.data;
}

// Adopte des cartes listées par le suivi (elles rejoignent le catalogue avec
// le scan Pokécardex comme image, en attendant que TCGdex les rattrape).
export async function adoptPokecardexCards(code, nums) {
  const res = await axios.post(`${API}/pokecardex/adopt`, { code, nums });
  return res.data;
}

// ── Pokédex (espèces possédées, par région) ──
export async function fetchPokedex(friendId) {
  const res = await axios.get(`${API}/pokedex`, { params: friendId ? { friend_id: friendId } : {} });
  return res.data;
}

// ── Recherche globale (séries, sets, cartes, illustrateurs) ──
export async function searchGlobal(q, limit) {
  const res = await axios.get(`${API}/search`, { params: { q, ...(limit ? { limit } : {}) } });
  return res.data;
}

// ── Diagnostic (cartes mal renseignées) ──
export async function fetchDebugCards({ issue, setId, limit } = {}) {
  const params = {};
  if (issue) params.issue = issue;
  if (setId) params.set_id = setId;
  if (limit) params.limit = limit;
  const res = await axios.get(`${API}/debug/cards`, { params });
  return res.data;
}

// ── Réglages de la sidebar (par utilisateur) ──
export async function fetchSidebarPreferences() {
  const res = await axios.get(`${API}/sidebar-preferences`);
  return res.data;
}

export async function saveSidebarPreferences(prefs) {
  await axios.put(`${API}/sidebar-preferences`, prefs);
}

// ── Scanner (reconnaissance par l'image, cf. lib/scanner/) ──
export async function scanRecognize(imageBase64) {
  // Timeout explicite : détection + empreintes de plusieurs cartes peuvent
  // prendre quelques secondes côté serveur.
  const res = await axios.post(`${API}/scan/recognize`, { image_base64: imageBase64 }, { timeout: 60000 });
  return res.data; // { fallback, cards: [{ quad, confidence, candidates: [...] }] }
}

export async function fetchScanManifest() {
  const res = await axios.get(`${API}/scan/manifest`);
  return res.data;
}

export const scanFileUrl = (name) => `${API}/scan/files/${name}`;

export async function fetchSet(setId) {
  return withOfflineFallback(async () => {
    const res = await axios.get(`${API}/sets/${setId}`);
    return res.data.set;
  }, `sets/${setId}.json`);
}

// ── Cartes ──
export async function fetchCard(cardId) {
  try {
    const res = await axios.get(`${API}/cards/${cardId}`);
    return res.data.card;
  } catch (err) {
    // Pas de fichier dédié par carte (ça fragmenterait le cache) : on la
    // retrouve dans le set déjà en cache local, dérivé de son id — même
    // règle que server.py card_set_id() (le préfixe avant le dernier "-").
    const setId = cardId.slice(0, cardId.lastIndexOf("-"));
    const set = await readJson(`sets/${setId}.json`);
    const card = set?.cards?.find(c => c.id === cardId);
    if (card) return card;
    throw err;
  }
}

// Historique de prix d'une carte (permanent). source = "tcgdex" (théorique)
// ou "os" (réel, à venir via l'Œil de Sauron).
export async function fetchCardPriceHistory(cardId, source = "tcgdex") {
  const res = await axios.get(`${API}/cards/${cardId}/price-history`, { params: { source } });
  return res.data.points || [];
}

// Observations de prix RÉELLES Cardmarket (Œil de Sauron) : jusqu'à 5 prix
// par état (PO/PL/LP/GD/EX/NM/MT), capturées par l'extension navigateur.
export async function fetchCardPriceObservations(cardId) {
  const res = await axios.get(`${API}/cards/${cardId}/price-observations`);
  return res.data.conditions || {};
}
// Série temporelle des captures Œil de Sauron par état ({NM: [{day, min,
// max, median, n}]}) + cote Cardmarket relevée les mêmes jours.
export async function fetchCardPriceObservationHistory(cardId, language = "fr") {
  const res = await axios.get(`${API}/cards/${cardId}/price-observations/history`, { params: { language } });
  return res.data;
}
// Vue d'ensemble Œil de Sauron : toutes les cartes observées pour un état.
export async function fetchOeilOverview({ condition = "NM", language = "fr" } = {}) {
  const res = await axios.get(`${API}/oeil-de-sauron/overview`, { params: { condition, language } });
  return res.data;
}

// ── Recherche globale (vue "Explorer") ──
// `excludeTcgp` retire les cartes TCG Pocket (jeu à part) : utilisé quand on
// arrive depuis le Pokédex ou le nom d'une carte, pas en navigation libre.
export async function browseCards({ name, rarity, illustrator, category, page = 1, excludeTcgp } = {}) {
  const res = await axios.get(`${API}/cards/browse`, {
    params: { name, rarity, illustrator, category, page, ...(excludeTcgp ? { exclude_tcgp: true } : {}) },
  });
  return res.data;
}

export async function fetchFilterOptions() {
  const res = await axios.get(`${API}/meta/filter-options`);
  return res.data;
}

// ── Collection ──
export async function fetchCollectionCardIds(friendId) {
  if (friendId) {
    const res = await axios.get(`${API}/collection/card-ids`, { params: { friend_id: friendId } });
    return { collection: res.data.collection || {}, conditions: res.data.conditions || {} };
  }
  return withOfflineFallback(async () => {
    const res = await axios.get(`${API}/collection/card-ids`);
    return { collection: res.data.collection || {}, conditions: res.data.conditions || {} };
  }, "collection-status.json");
}

export async function fetchCollectionStats(friendId) {
  if (friendId) {
    const res = await axios.get(`${API}/collection/stats`, { params: { friend_id: friendId } });
    return res.data;
  }
  return withOfflineFallback(async () => {
    const res = await axios.get(`${API}/collection/stats`);
    return res.data;
  }, "collection-stats.json");
}

// Toutes les cartes marquées « recherchée », en cartes complètes.
// ⚠️ Vues « lourdes » (2026-09-14) : le serveur résout CHAQUE set possédé
// (159 sets pour VenusJ → 9 s mesurées côté serveur) — avec le timeout
// général de 10 s, une grosse collection sur réseau mobile expirait juste
// avant la réponse. Pas de repli hors ligne pour ces vues, donc un délai
// long ne coûte rien à l'usage : on attend simplement la vraie réponse.
const HEAVY_VIEW_TIMEOUT = 60000;

export async function fetchWantedCards() {
  const res = await axios.get(`${API}/collection/wanted-cards`, { timeout: HEAVY_VIEW_TIMEOUT });
  return res.data.cards || [];
}

// Toute la collection possédée, une entrée par variante possédée.
export async function fetchOwnedCards() {
  const res = await axios.get(`${API}/collection/owned-cards`, { timeout: HEAVY_VIEW_TIMEOUT });
  return res.data.cards || [];
}

// Collection classée par valeur + cartes en hausse récente.
export async function fetchCollectionValue() {
  const res = await axios.get(`${API}/collection/value`, { timeout: HEAVY_VIEW_TIMEOUT });
  return { cards: [], total: 0, risers: [], ...res.data };
}

// ── Sauvegarde ──
// Télécharge la sauvegarde en JSON. Le fichier est fabriqué côté navigateur
// (Blob + lien temporaire) : passer par une URL directe ferait perdre l'en-tête
// d'authentification, que seul l'intercepteur axios ajoute.
export async function downloadBackup() {
  const res = await axios.get(`${API}/backup/export`);
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([JSON.stringify(res.data, null, 1)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `poketracker-sauvegarde-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  const counts = res.data?.collections || {};
  return Object.values(counts).reduce((n, arr) => n + (arr?.length || 0), 0);
}

export async function uploadBackup(data, mode = "merge") {
  const res = await axios.post(`${API}/backup/import`, { data, mode });
  return res.data;
}

// Une carte au hasard qu'il reste à obtenir (tuile d'accueil).
export async function fetchRandomMissingCard() {
  const res = await axios.get(`${API}/collection/random-missing`);
  return res.data.card || null;
}

export async function fetchCollectionCompletion(friendId) {
  if (friendId) {
    const res = await axios.get(`${API}/collection/completion`, { params: { friend_id: friendId } });
    return res.data.sets || [];
  }
  return withOfflineFallback(async () => {
    // 4,6 s côté serveur pour 159 sets (2026-09-14) : 10 s + latence mobile
    // était trop juste, et un timeout ici ressert la progression en cache.
    const res = await axios.get(`${API}/collection/completion`, { timeout: 25000 });
    return res.data.sets || [];
  }, "collection-completion.json");
}

// ── Jetons dédiés à l'extension Œil de Sauron (Réglages) ──
// Sans expiration, révocables ; un jeton existant peut être réaffiché.
export async function fetchExtensionTokens() {
  const res = await axios.get(`${API}/auth/extension-tokens`);
  return res.data.tokens || [];
}

export async function createExtensionToken(name) {
  const res = await axios.post(`${API}/auth/extension-tokens`, { name: name || null });
  return res.data; // { id, name, created_at, last_used_at, token }
}

export async function revealExtensionToken(id) {
  const res = await axios.post(`${API}/auth/extension-tokens/${encodeURIComponent(id)}/reveal`);
  return res.data; // { id, name, created_at, last_used_at, token }
}

export async function revokeExtensionToken(id) {
  await axios.delete(`${API}/auth/extension-tokens/${encodeURIComponent(id)}`);
}

// ── Amis (partage de collection en lecture seule) ──
export async function fetchFriendCode() {
  const res = await axios.get(`${API}/friends/code`);
  return res.data.code;
}

export async function regenerateFriendCode() {
  const res = await axios.post(`${API}/friends/code/regenerate`);
  return res.data.code;
}

export async function addFriendByCode(code) {
  const res = await axios.post(`${API}/friends/add`, { code });
  return res.data; // { friend_id, username }
}

export async function fetchFriends() {
  const res = await axios.get(`${API}/friends`);
  return res.data.friends || [];
}

export async function removeFriendApi(friendId) {
  await axios.delete(`${API}/friends/${friendId}`);
}

// ── Anomalies de prix (cartes très au-dessus/en-dessous de la médiane de
// leur groupe set+rareté) ──
export async function fetchPriceAnomalies() {
  const res = await axios.get(`${API}/market/price-anomalies`);
  return { expensive: [], underpriced: [], ...res.data };
}

// ── Préférence Set normal / Master Set (par set) ──
export async function fetchSetPreferences(friendId) {
  const res = await axios.get(`${API}/set-preferences`, { params: friendId ? { friend_id: friendId } : {} });
  return res.data || {};
}

export async function setSetPreference(setId, mode) {
  await axios.put(`${API}/set-preferences/${setId}`, { mode });
}

export async function addToCollection(cardId, variant, status, quantity = 1) {
  await axios.post(`${API}/collection`, { card_id: cardId, variant, status, quantity });
}

export async function removeFromCollection(cardId, variant) {
  await axios.delete(`${API}/collection/${cardId}/${variant}`);
}

export async function bulkSetStatus(items, status) {
  await axios.post(`${API}/collection/bulk`, { items, status });
}

export async function bulkRemoveFromCollection(items) {
  await axios.post(`${API}/collection/bulk-remove`, { items });
}

// ── État (grade Cardmarket) d'une carte possédée ──
export async function fetchCollectionItem(cardId, variant) {
  const res = await axios.get(`${API}/collection/${cardId}/${variant}`);
  return res.data;
}

export async function setCollectionItemCondition(cardId, variant, condition) {
  await axios.put(`${API}/collection/${cardId}/${variant}/condition`, { condition });
}

// ── Overrides variants (manuel) ──
export async function setCardVariantOverride(cardId, variants) {
  await axios.put(`${API}/cards/${cardId}/variants`, { variants });
}

export async function clearCardVariantOverride(cardId) {
  await axios.delete(`${API}/cards/${cardId}/variants`);
}

export async function setSetVariantRules(setId, rules) {
  await axios.put(`${API}/sets/${setId}/variant-rules`, { rules });
}

// ── Vue transversale (plusieurs séries/sets à la fois) ──
export async function fetchCrossViewCards({ setIds, rarity, variant, category }) {
  const res = await axios.get(`${API}/cards/cross-view`, {
    params: { set_ids: setIds, rarity, variant, category },
    paramsSerializer: { indexes: null },
  });
  return res.data;
}

// ── Listes personnalisées ──
export async function fetchLists(friendId) {
  const res = await axios.get(`${API}/lists`, { params: friendId ? { friend_id: friendId } : {} });
  return res.data.lists || [];
}

// Privée par défaut : { private: false } la rend visible aux amis via
// fetchLists/fetchListCards(friendId).
export async function setListPrivacy(listId, isPrivate) {
  const res = await axios.put(`${API}/lists/${listId}/private`, { private: isPrivate });
  return res.data;
}

export async function createList(name) {
  const res = await axios.post(`${API}/lists`, { name });
  return res.data;
}

export async function renameList(listId, name) {
  await axios.put(`${API}/lists/${listId}`, { name });
}

export async function deleteList(listId) {
  await axios.delete(`${API}/lists/${listId}`);
}

export async function addItemsToList(listId, items) {
  await axios.post(`${API}/lists/${listId}/items`, { items });
}

export async function removeItemsFromList(listId, items) {
  await axios.post(`${API}/lists/${listId}/items/remove`, { items });
}

export async function fetchListCards(listId, friendId) {
  const res = await axios.get(`${API}/lists/${listId}/cards`, { params: friendId ? { friend_id: friendId } : {} });
  return res.data;
}

// ── Balls de rareté ──
// Table GLOBALE rareté -> ball (identité visuelle de l'appli, pas une
// préférence perso) : lecture publique, écriture réservée aux admins
// (le backend renvoie 403 sinon).
export async function fetchRarityBalls() {
  const res = await axios.get(`${API}/rarity-balls`);
  return res.data.assignments || {};
}

export async function setRarityBall(key, ball) {
  await axios.put(`${API}/rarity-balls/${encodeURIComponent(key)}`, { ball });
}

export async function deleteRarityBall(key) {
  await axios.delete(`${API}/rarity-balls/${encodeURIComponent(key)}`);
}

// ── Variantes perso (custom variants) ──
// Catalogue de variantes créées par l'utilisateur (ex. Reverse Poké Ball),
// appliquées à une carte ou à un set. Le backend les attache ensuite aux
// cartes servies (champ card.custom_variants), donc l'affichage n'a rien à
// recalculer — ces fonctions servent à gérer le catalogue et les applications.
export async function fetchCustomVariants() {
  const res = await axios.get(`${API}/custom-variants`);
  return { variants: res.data.variants || [], applications: res.data.applications || [] };
}

export async function createCustomVariant({ name, abbr, color }) {
  const res = await axios.post(`${API}/custom-variants`, { name, abbr, color });
  return res.data;
}

export async function updateCustomVariant(variantId, { name, abbr, color }) {
  await axios.put(`${API}/custom-variants/${variantId}`, { name, abbr, color });
}

export async function deleteCustomVariant(variantId) {
  await axios.delete(`${API}/custom-variants/${variantId}`);
}

export async function applyCustomVariant(variantId, { scope, cardId, setId, rarity }) {
  const res = await axios.post(`${API}/custom-variants/${variantId}/apply`, {
    scope, card_id: cardId, set_id: setId, rarity,
  });
  return res.data;
}

export async function removeCustomVariantApplication(appId) {
  await axios.delete(`${API}/custom-variants/apps/${appId}`);
}

// ── Règles « réservé au Master Set + » (par compte) ──
export async function fetchMasterSetRules() {
  const res = await axios.get(`${API}/master-set-rules`);
  return res.data.rules || [];
}

export async function addMasterSetRule(rule) {
  const res = await axios.post(`${API}/master-set-rules`, rule);
  return res.data;
}

export async function removeMasterSetRule(ruleId) {
  await axios.delete(`${API}/master-set-rules/${ruleId}`);
}

// ── App mobile (auto-update) ──
// Pas de token requis : même JS que la version web, rien de sensible.
export async function fetchAppVersion() {
  const res = await axios.get(`${API}/app/version`);
  return res.data;
}

// ── Investissement (admin uniquement, 2026-09-28) ──
export async function fetchInvestments() {
  const res = await axios.get(`${API}/investments`);
  return res.data.items;
}
export async function createInvestment(payload) {
  const res = await axios.post(`${API}/investments`, payload);
  return res.data.item;
}
export async function updateInvestment(id, payload) {
  const res = await axios.put(`${API}/investments/${id}`, payload);
  return res.data.item;
}
export async function deleteInvestment(id) {
  await axios.delete(`${API}/investments/${id}`);
}
export async function fetchSealedProducts() {
  const res = await axios.get(`${API}/sealed-products`);
  return res.data.items;
}
export async function createSealedProduct(payload) {
  const res = await axios.post(`${API}/sealed-products`, payload);
  return res.data.item;
}
export async function updateSealedProduct(id, payload) {
  const res = await axios.put(`${API}/sealed-products/${id}`, payload);
  return res.data.item;
}
export async function deleteSealedProduct(id) {
  await axios.delete(`${API}/sealed-products/${id}`);
}
// `q` : un nom (français ou anglais) OU une URL de fiche Cardmarket collée
// telle quelle (correspondance exacte côté serveur).
export async function searchCardmarketSealed(q) {
  const res = await axios.get(`${API}/cardmarket/sealed-search`, { params: { q } });
  return res.data.items;
}
// Fiches scellées visitées avec Œil de Sauron et pas encore liées à un produit.
export async function fetchCardmarketSealedSeen() {
  const res = await axios.get(`${API}/cardmarket/sealed-seen`);
  return res.data.items;
}
export async function addSealedPrice(id, price, date) {
  const res = await axios.post(`${API}/sealed-products/${id}/price`, { price, date });
  return res.data;
}
export async function fetchSealedPriceHistory(id) {
  const res = await axios.get(`${API}/sealed-products/${id}/price-history`);
  return res.data.points;
}

export { API };
